
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BuyerLayout from "@/components/layouts/BuyerLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Info, ArrowRight, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { associationApi, documentApi, enhancedDocumentApi } from "@/services/api";

const DocumentRequest = () => {
  const { associationId } = useParams<{ associationId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  
  // Fetch association data
  const { data: association, isLoading: associationLoading, error: associationError } = useQuery({
    queryKey: ['association', associationId],
    queryFn: () => associationId ? associationApi.getById(parseInt(associationId)) : Promise.reject('No association ID'),
    enabled: !!associationId,
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading association",
          description: error.message || "Failed to load association details"
        });
      }
    }
  });

  // Fetch documents - now extracting documents array from response
  const { data: documentsResponse, isLoading: documentsLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: enhancedDocumentApi.list,
    meta: {
      onError: (error: Error) => {
        toast({
          variant: "destructive",
          title: "Error loading documents",
          description: error.message,
        });
      }
    }
  });

  // Extract documents array from the response
  const documents = documentsResponse?.data || [];

  const isLoading = associationLoading || documentsLoading;

  if (associationError || (!association && !isLoading)) {
    return (
      <BuyerLayout>
        <div className="space-y-6">
          <h1 className="text-3xl font-bold tracking-tight">Association Not Found</h1>
          <p className="text-gray-500">
            The association you're looking for could not be found. Please try another search.
          </p>
          <Button onClick={() => navigate("/buyer/search")}>Back to Search</Button>
        </div>
      </BuyerLayout>
    );
  }

  const handleToggleDocument = (documentId: number) => {
    setSelectedDocuments(prev => 
      prev.includes(documentId)
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const handleContinue = () => {
    if (selectedDocuments.length === 0) {
      toast({
        variant: "destructive",
        title: "No documents selected",
        description: "Please select at least one document to continue."
      });
      return;
    }

    // In a real app, you'd create an order in the backend and get an order ID
    const mockOrderId = "order-" + Math.floor(Math.random() * 1000);
    navigate(`/buyer/payment/${mockOrderId}`);
  };

  // Calculate the total price of selected documents (simplified)
  const totalAmount = selectedDocuments.reduce((sum, docId) => {
    const doc = documents.find(d => d.id === docId);
    return sum + (doc ? 25 : 0); // Default price as documents don't have price in the model
  }, 0);

  if (isLoading) {
    return (
      <BuyerLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
          <span className="ml-2">Loading...</span>
        </div>
      </BuyerLayout>
    );
  }

  return (
    <BuyerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Request Documents</h1>
          <p className="text-gray-500">
            Select documents from {association?.name}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Association Information</CardTitle>
            <CardDescription>
              Please review the association details
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div>
                <h3 className="font-medium">Name</h3>
                <p>{association?.name}</p>
              </div>
              <div>
                <h3 className="font-medium">Address</h3>
                <p>{association?.default_property_address || 'Not provided'}</p>
              </div>
              <div>
                <h3 className="font-medium">Type</h3>
                <p>{association?.association_type}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Available Documents</CardTitle>
            <CardDescription>
              Select the documents you need
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {documents.length > 0 ? (
                documents.map((doc) => (
                  <div key={doc.id} className="flex items-start space-x-3 p-3 border rounded-md">
                    <Checkbox
                      id={`doc-${doc.id}`}
                      checked={selectedDocuments.includes(doc.id)}
                      onCheckedChange={() => handleToggleDocument(doc.id)}
                    />
                    <div className="flex-1">
                      <label
                        htmlFor={`doc-${doc.id}`}
                        className="text-sm font-medium leading-none cursor-pointer flex items-center"
                      >
                        <FileText className="h-4 w-4 mr-2 text-brand-blue" />
                        {doc.name}
                      </label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Document Type: {doc.document_type_id}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="font-medium">$25.00</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <Info className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No documents available for this association</p>
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col sm:flex-row items-center justify-between border-t p-4">
            <div className="mb-4 sm:mb-0">
              <p className="text-sm text-muted-foreground">
                Selected: <span className="font-medium">{selectedDocuments.length}</span> document
                {selectedDocuments.length !== 1 ? "s" : ""}
              </p>
              <p className="text-lg font-bold">
                Total: ${totalAmount.toFixed(2)}
              </p>
            </div>
            <div className="flex space-x-2">
              <Button variant="outline" onClick={() => navigate("/buyer/search")}>
                Back
              </Button>
              <Button onClick={handleContinue}>
                Continue to Payment
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>
    </BuyerLayout>
  );
};

export default DocumentRequest;
